import { observer } from 'mobx-react-lite'
import { ContentError } from '@/components/ContentError'
import { PtinRequiredView } from '@/features/iam/providers/components/PtinRequiredView'
import { WorkspaceFilterViewHeadingContainer } from '@/components/WorkspaceFilterViewHeadingContainer'
import { useDocumentTitle } from '@/hooks/document-title'
import { useHotkey } from '@/hooks/keyboard-hooks'
import { useActivation } from '@/hooks/view-model-hooks'
import { UpcomingConsultationPreviewContainer } from '@/features/jobs/list/components/UpcomingConsultationPreview/UpcomingConsultationPreviewContainer'
import { type JobManagementViewModel } from '@/features/jobs/list/JobManagementViewModel'
import {
  JobListContainer,
  JobListTableContainer,
} from '@/features/jobs/list/components/JobManagement/JobList/JobListContainer'
import { JobListSkeleton } from '@/features/jobs/list/components/JobManagement/JobList'
import { JobListPage } from './JobListPage'
import { JobManagementDialogsContainer } from './JobManagementDialogsContainer'
import { useFeatures } from '@/contexts/root-store-context'

export interface JobListPageContainerProps {
  vm: JobManagementViewModel
}

export const JobListPageContainer = observer(function JobListPageContainer({
  vm,
}: JobListPageContainerProps) {
  const { shellInfo } = useFeatures(({ shell }) => ({
    shellInfo: shell.shellInfo,
  }))
  useDocumentTitle('My jobs')
  useActivation(vm)
  useHotkey(['Escape'], vm.searcher.searchText.clear)

  if (vm.ptinRequired) {
    return <PtinRequiredView corporateMode={shellInfo.corporateMode} />
  }

  return (
    <JobListPage
      heading={
        <WorkspaceFilterViewHeadingContainer
          allOptionText="All workspaces"
          primaryText="My jobs"
          filter={vm.searcher.workspacesFilter}
          testId="workspace-filter-view-heading-dropdown"
        />
      }
    >
      {vm.fetch.match({
        pending: () => (
          // The visible columns and their widths are the user's saved layout,
          // so the skeleton reuses the real table to line up with it.
          <JobListSkeleton table={<JobListTableContainer vm={vm} />} />
        ),
        rejected: (err) => <ContentError error={err} />,
        resolved: () => (
          <>
            <UpcomingConsultationPreviewContainer
              vm={vm.upcomingConsultationPreviewVm}
            />
            <JobListContainer vm={vm} />
            <JobManagementDialogsContainer vm={vm} />
          </>
        ),
      })}
    </JobListPage>
  )
})
